import React, { useMemo } from "react";
import { Autocomplete, Box, Button, CircularProgress, FormControl, MenuItem, Select, TextField, Typography } from "@mui/material";
import ApartmentIcon from "@mui/icons-material/Apartment";
import RestartAltIcon from "@mui/icons-material/RestartAlt";

const ACCENT = "#26890d";
const ACTION_BLUE = "#006eff";

// Two-dropdown scope filter for the Enterprise Usage page — mirrors
// CostCenterSelector.jsx's visual pattern (used on the org-scoped dashboard)
// but with an extra "category" dropdown since this page can scope by either
// Organization or Cost Center, not just cost center within a fixed org.
// A–Z by name, the way a person reads a list: ignoring case, ignoring punctuation and spacing (so "Deloitte - FR - A&A"
// sorts by its letters, after "Deloitte Argentina", not ahead of every name that starts with a letter), and treating
// digits as numbers (so "Team 2" comes before "Team 10"). A missing name sorts first rather than breaking the list.
const labelCollator = new Intl.Collator(undefined, { sensitivity: "base", numeric: true, ignorePunctuation: true });
export const sortByLabel = (options, labelKey) =>
  [...(options ?? [])].sort((a, b) => labelCollator.compare(String(a?.[labelKey] ?? ""), String(b?.[labelKey] ?? "")));

const EnterpriseScopeSelector = ({ category, value, orgOptions, costCenterOptions, orgNameById = {}, loading, onCategoryChange, onValueChange, onReset, hideEnterpriseOption = false, hideCostCenterOption = false }) => {
  const valueKey = category === "org" ? "org_id" : "cost_center_id";
  const valueLabelKey = category === "org" ? "org_name" : "cost_center_name";
  // The list is shown alphabetically, whatever order the usage data arrives in (it is ordered by usage).
  const rawOptions = category === "org" ? orgOptions : category === "costCenter" ? costCenterOptions : [];
  const valueOptions = useMemo(() => sortByLabel(rawOptions, valueLabelKey), [rawOptions, valueLabelKey]);
  const selectedOption = valueOptions.find((o) => o[valueKey] === value) ?? null;
  const isScoped = category !== "enterprise" && Boolean(value);
  // orgOptions is usage-derived (org_breakdown) and only lists orgs with
  // activity in the current date range — an org scoped-to that has zero
  // usage (e.g. after narrowing the date filter) falls out of that list even
  // while still selected, so its name is resolved from the full active-org
  // list instead of showing "Loading…" indefinitely.
  const selectedLabel = selectedOption?.[valueLabelKey] ?? (category === "org" ? orgNameById[value] : undefined);

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, px: 3, py: 2, mb: 3, backgroundColor: "#fff", border: "1px solid #e8e8e8", borderRadius: "12px", flexWrap: "wrap" }}>
      <Typography sx={{ fontSize: "0.85rem", fontWeight: 700, color: "#555", whiteSpace: "nowrap" }}>
        Filter by
      </Typography>

      <FormControl size="small" sx={{ minWidth: 180 }}>
        <Select
          value={category}
          onChange={(e) => onCategoryChange(e.target.value)}
          sx={{ backgroundColor: "#f9f9f9", borderRadius: "6px", fontSize: "0.85rem" }}
        >
          {!hideEnterpriseOption && (
            <MenuItem value="enterprise" sx={{ fontSize: "0.85rem", fontWeight: 600 }}>
              Enterprise (all orgs)
            </MenuItem>
          )}
          <MenuItem value="org" sx={{ fontSize: "0.85rem" }}>Organization</MenuItem>
          {!hideCostCenterOption && (
            <MenuItem value="costCenter" sx={{ fontSize: "0.85rem" }}>Cost Center</MenuItem>
          )}
        </Select>
      </FormControl>

      {category !== "enterprise" && (
        // Type to search the list (A–Z), or open it and pick. A selected item that has dropped out of the list (an
        // organization with no usage in a narrowed date range) is kept in it so it still shows its name.
        <Autocomplete
          size="small"
          disableClearable
          options={valueOptions}
          value={selectedOption ?? (value ? { [valueKey]: value, [valueLabelKey]: selectedLabel ?? "Loading…" } : null)}
          getOptionLabel={(option) => option?.[valueLabelKey] ?? ""}
          isOptionEqualToValue={(option, selected) => option[valueKey] === selected[valueKey]}
          onChange={(_, option) => onValueChange(option?.[valueKey] ?? null)}
          noOptionsText={category === "org" ? "No matching organization" : "No matching cost center"}
          componentsProps={{ popper: { style: { width: "fit-content", minWidth: 260 } } }}
          renderOption={(props, option) => (
            <li {...props} key={option[valueKey]} style={{ fontSize: "0.85rem" }}>
              {option[valueLabelKey]}
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={category === "org" ? "Select an organization…" : "Select a cost center…"}
              inputProps={{ ...params.inputProps, "aria-label": category === "org" ? "Organization" : "Cost center" }}
            />
          )}
          sx={{ width: 280, "& .MuiOutlinedInput-root": { backgroundColor: "#f9f9f9", borderRadius: "6px", fontSize: "0.85rem" } }}
        />
      )}

      {loading && <CircularProgress size={18} sx={{ color: ACCENT }} />}

      {(category !== "enterprise" || value) && (
        <Button
          size="small"
          startIcon={<RestartAltIcon sx={{ fontSize: "16px !important" }} />}
          onClick={onReset}
          sx={{ textTransform: "none", fontSize: "0.8rem", fontWeight: 500, color: "#666", "&:hover": { backgroundColor: "#f0f0f0" } }}
        >
          Reset
        </Button>
      )}

      <Box sx={{ ml: "auto", display: "flex", alignItems: "center", gap: 0.6, px: 1.2, py: 0.35, backgroundColor: isScoped ? "rgba(0,110,255,0.07)" : "rgba(38,137,13,0.07)", border: `1px solid ${isScoped ? "rgba(0,110,255,0.25)" : "rgba(38,137,13,0.22)"}`, borderRadius: "20px" }}>
        <ApartmentIcon sx={{ fontSize: "13px", color: isScoped ? ACTION_BLUE : ACCENT }} />
        <Typography sx={{ fontSize: "12px", fontWeight: 700, color: isScoped ? ACTION_BLUE : ACCENT }}>
          {isScoped
            ? `${category === "org" ? "Organization" : "Cost Center"}: ${selectedLabel ?? "Loading…"}`
            : hideEnterpriseOption
              ? "No organization selected"
              : "Enterprise-wide"}
        </Typography>
      </Box>
    </Box>
  );
};

export default EnterpriseScopeSelector;
